import { isValidElement, type ReactNode } from "react";
import { create } from "zustand";
import type { NavBarChipRow, NavBarRegistration, NavBarTabs } from "./types";

/** The tab id a host with one tab (the web) uses when no `NavBarScope` is mounted. */
export const DEFAULT_TAB_ID = "main";

export interface NavBarEntry {
    tabId: string;
    routeKey: string;
    /** Registration order. The newest entry of the active tab is the one the bar shows. */
    seq: number;
    registration: NavBarRegistration;
}

interface NavBarState {
    entries: Record<string, NavBarEntry>;
    register(tabId: string, routeKey: string, registration: NavBarRegistration): void;
    unregister(tabId: string, routeKey: string): void;
}

let lastSeq = 0;

function parentsEqual(
    left: NavBarRegistration["parent"],
    right: NavBarRegistration["parent"],
): boolean {
    if (left === right) return true;
    if (!left || !right) return false;
    return left.label === right.label && left.onSelect === right.onSelect;
}

type ComparableElementProps = Record<string, unknown> & { children?: ReactNode };

function nodesShallowEqual(left: ReactNode, right: ReactNode): boolean {
    if (Object.is(left, right)) return true;
    if (Array.isArray(left) || Array.isArray(right)) {
        return (
            Array.isArray(left) &&
            Array.isArray(right) &&
            left.length === right.length &&
            left.every((child, index) => nodesShallowEqual(child, right[index]))
        );
    }
    if (
        !isValidElement<ComparableElementProps>(left) ||
        !isValidElement<ComparableElementProps>(right) ||
        left.type !== right.type ||
        left.key !== right.key
    ) {
        return false;
    }
    const leftKeys = Object.keys(left.props);
    const rightKeys = Object.keys(right.props);
    return (
        leftKeys.length === rightKeys.length &&
        leftKeys.every(
            (key) =>
                Object.hasOwn(right.props, key) &&
                (key === "children"
                    ? nodesShallowEqual(left.props.children, right.props.children)
                    : Object.is(left.props[key], right.props[key])),
        )
    );
}

function chipsEqual(left: NavBarChipRow | undefined, right: NavBarChipRow | undefined): boolean {
    if (left === right) return true;
    if (!left || !right) return false;
    return (
        left.onClear === right.onClear &&
        left.chips.length === right.chips.length &&
        left.chips.every((chip, index) => {
            const other = right.chips[index];
            return (
                chip.key === other.key &&
                chip.facet === other.facet &&
                chip.label === other.label &&
                chip.onRemove === other.onRemove
            );
        })
    );
}

function tabsEqual(left: NavBarTabs | undefined, right: NavBarTabs | undefined): boolean {
    if (left === right) return true;
    if (!left || !right) return false;
    return (
        left.label === right.label &&
        left.value === right.value &&
        left.onChange === right.onChange &&
        left.items.length === right.items.length &&
        left.items.every(
            (item, index) =>
                item.key === right.items[index].key && item.label === right.items[index].label,
        )
    );
}

export function navBarRegistrationsShallowEqual(
    left: NavBarRegistration,
    right: NavBarRegistration,
): boolean {
    return (
        left === right ||
        (left.title === right.title &&
            nodesShallowEqual(left.titleNode, right.titleNode) &&
            parentsEqual(left.parent, right.parent) &&
            nodesShallowEqual(left.status, right.status) &&
            nodesShallowEqual(left.description, right.description) &&
            nodesShallowEqual(left.filterBar, right.filterBar) &&
            nodesShallowEqual(left.actions, right.actions) &&
            nodesShallowEqual(left.state, right.state) &&
            chipsEqual(left.chips, right.chips) &&
            nodesShallowEqual(left.selection, right.selection) &&
            tabsEqual(left.tabs, right.tabs) &&
            left.hidden === right.hidden)
    );
}

/**
 * Registrations keyed by `{ tabId, routeKey }`. `tabId` is the nav bar SCOPE the
 * registering mount sits in (`NavBarScope`), not necessarily a workspace tab: a hidden
 * keep-alive mount keeps its entry here, and `selectActiveRegistration` never returns it
 * while another scope is the visible one.
 */
export const useNavBarStore = create<NavBarState>()((set) => ({
    entries: {},
    register: (tabId, routeKey, registration) =>
        set((state) => {
            const key = `${tabId}\u0000${routeKey}`;
            const current = state.entries[key];
            if (current && navBarRegistrationsShallowEqual(current.registration, registration))
                return state;
            // An update keeps its place. A newer nested registration still wins.
            const seq = current?.seq ?? ++lastSeq;
            return { entries: { ...state.entries, [key]: { tabId, routeKey, seq, registration } } };
        }),
    unregister: (tabId, routeKey) =>
        set((state) => {
            const key = `${tabId}\u0000${routeKey}`;
            if (!state.entries[key]) return state;
            const entries = { ...state.entries };
            delete entries[key];
            return { entries };
        }),
}));

/**
 * The registration the bar shows: the newest one of the VISIBLE scope, or none.
 *
 * The rule: `activeTabId` is the scope of the content the user sees, and only that
 * scope's registrations compete. `seq` arbitrates nested registrations inside one
 * visible mount (a screen and the sub-screen it hosts), never a hidden mount against a
 * visible one: a mount the host keeps alive but hidden (the desktop's keep-alive
 * Questions slot, the browser screen under an open document) MUST register into a scope
 * of its own, so a late registration from it -- Overview mounting behind the tenant cache
 * hydration after the document tab already registered -- can never take the bar
 * (`WorkspaceShell.tsx`'s `navScopeForRoute` is the one place the desktop maps a route
 * to its scope; seen live on the packaged WKWebView build, 2026-09-04, as the crumb
 * "Local workspace / Home" over an open document).
 */
export function selectActiveRegistration(
    entries: Record<string, NavBarEntry>,
    activeTabId: string,
): NavBarRegistration | null {
    let newest: NavBarEntry | null = null;
    for (const entry of Object.values(entries)) {
        if (entry.tabId === activeTabId && (newest === null || entry.seq > newest.seq)) {
            newest = entry;
        }
    }
    return newest?.registration ?? null;
}
